import React, { useEffect, useRef, useState } from 'react'
import { highlightAll } from 'prismjs'
import ClipboardJS from 'clipboard'

import 'prismjs/components/prism-json'
import 'prismjs/components/prism-java'
import 'prismjs/components/prism-bash'

import Container from './Container'
import styles from './styles.module.css'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const SampleCode = ({
  copy = false,
  children,
  className = '',
  language,
  method,
  renderOptions,
  sticky = false,
  hideheader = false,
  title,
}) => {
  const { t } = useTranslation()
  const content = children[0].trim()
  const copyBtnEl = useRef(null)
  const titleTxtEl = useRef(null)
  const [mouseOverCopy, setMouseOverCopy] = useState(false)

  useEffect(() => {
    if (copy) {
      new ClipboardJS(copyBtnEl.current, { text: () => content })
      if (title) {
        new ClipboardJS(titleTxtEl.current, {
          text: () => titleTxtEl.current.innerText,
        })
      }
    }
    highlightAll()
  }, [content, copy, title])

  const notifyCopy = () => {
    const config = {
      message: t('general.clipboard'),
      position: 'top right',
    }
    window.dispatchEvent(new CustomEvent('toastsuccess', { detail: config }))
  }

  const copyTitle = () => {
    if (copy) {
      const selection = window.getSelection()
      const range = document.createRange()
      range.selectNodeContents(titleTxtEl.current)
      selection.removeAllRanges()
      selection.addRange(range)
      notifyCopy()
    }
  }

  const handleKeyPress = (evt, callback) => {
    if (evt.keyCode === 13) {
      callback()
    }
  }

  const renderHeader = () => {
    return (
      <div className={styles.header}>
        <div className={styles.headerText}>
          {method && (
            <span className={`${styles.verb} ${styles[method.toLowerCase()]}`} data-testid="sample-code-method">
              {method}
            </span>
          )}
          {title && (
            <span
              className={styles.title}
              data-testid="sample-code-title"
              ref={titleTxtEl}
              role="button"
              onClick={() => copyTitle()}
              onKeyUp={evt => handleKeyPress(evt, copyTitle)}
              tabIndex={0}
            >
              {title}
            </span>
          )}
        </div>
        <div className={styles.actions}>
          {renderOptions && renderOptions()}
          {copy && (
            <span
              className={styles.copy}
              data-testid="hot-icon-copy-container"
              ref={copyBtnEl}
              role="button"
              onClick={() => notifyCopy()}
              onKeyPress={() => false}
              onMouseEnter={() => setMouseOverCopy(true)}
              onMouseLeave={() => setMouseOverCopy(false)}
              tabIndex={0}
            >
              <FontAwesomeIcon data-testid="hot-icon-copy" icon={[`fa${mouseOverCopy ? 's' : 'r'}`, 'copy']} />
            </span>
          )}
        </div>
      </div>
    )
  }

  const getLanguage = () => {
    if (language) {
      switch (language.toLowerCase()) {
        case 'node':
          return 'javascript'
        case 'curl':
          return 'bash'
        default:
          return language.toLowerCase()
      }
    }
    return null
  }

  const jsxcontent = (
    <div className={sticky ? styles.sticky : ''}>
      {!hideheader && renderHeader()}
      <pre className={`${styles.content} language-${getLanguage()}`} data-testid="sample-code-pre">
        <code className={`language-${getLanguage()}`} data-testid="sample-code-code">
          {content}
        </code>
      </pre>
    </div>
  )

  const renderWithClassName = () => <div className={className}>{jsxcontent}</div>

  return className !== '' ? renderWithClassName() : jsxcontent
}

export { Container }
export default SampleCode
